import { Handle, Position as FlowPosition } from "reactflow";

export interface AddressNodeData {
  address: string;
  isRoot: boolean;
  attribution: {
    label: string | null;
    confidence: "confirmed-fact" | "inferred" | "third-party-attribution" | "unknown";
    source: string | null;
  };
  collapsed: boolean;
  hiddenDescendantCount: number;
  hasChildren: boolean;
  expandable: boolean;
  onToggleCollapse: (address: string) => void;
  onExpand: (address: string) => void;
  expanding: boolean;
}

function shortAddress(a: string): string {
  return a.length > 20 ? `${a.slice(0, 10)}…${a.slice(-6)}` : a;
}

const CONFIDENCE_STYLES: Record<string, string> = {
  "confirmed-fact": "text-signal-400",
  "third-party-attribution": "text-ledger-400",
  inferred: "text-muted",
  unknown: "text-muted",
};

export default function AddressNode({ data }: { data: AddressNodeData }) {
  return (
    <div
      className={`rounded-md border px-3 py-2 text-xs font-mono bg-ink-900 min-w-[200px] ${
        data.isRoot ? "border-ledger-500" : "border-ink-700"
      }`}
    >
      <Handle type="target" position={FlowPosition.Left} />
      <p className={data.isRoot ? "text-ledger-400" : "text-paper"}>{shortAddress(data.address)}</p>
      {data.attribution.label && (
        <p className={`mt-1 ${CONFIDENCE_STYLES[data.attribution.confidence]}`}>
          {data.attribution.label} ({data.attribution.confidence})
        </p>
      )}
      <div className="mt-2 flex gap-2">
        {data.hasChildren && (
          <button
            onClick={() => data.onToggleCollapse(data.address)}
            className="text-muted hover:text-paper underline"
          >
            {data.collapsed ? `Show ${data.hiddenDescendantCount} hidden` : "Collapse"}
          </button>
        )}
        <button
          onClick={() => data.onExpand(data.address)}
          disabled={data.expanding}
          className={`text-muted hover:text-paper underline disabled:opacity-50 ${
            data.expandable ? "" : "hidden"
          }`}
        >
          {data.expanding ? "Expanding…" : "Expand further"}
        </button>
      </div>
      <Handle type="source" position={FlowPosition.Right} />
    </div>
  );
}
